// 261025 E3-P3d3 — hàm THUẦN của cổng AI ① «quét quyển» trong chuỗi truyện mới: thẻ «Chờ bạn quyết · Chặng 1/3» (WAIT_REVIEW_GATE trước khi mở khung xương),
// chữ từng quyển còn lỗi, hộp xác nhận «Chạy tiếp, để lỗi xem sau», và nhãn «lỗi ①» cho cột «Tình trạng» của bảng chương ở chặng Nguồn.
// KHÔNG import antd / react (jest của react-scripts không load được antd v6). `.tsx` chỉ vẽ và gọi API. FE KHÔNG tự suy trạng thái cổng: mọi số/mục do BE tính sẵn (`VolumeGateView`).
import type { VolumeGateView, VolumeGateViewItem } from '../../../types/adaptationWorkbench';

export const VOLUME_GATE_LABEL = 'Chờ bạn quyết · Chặng 1/3';
export const VOLUME_GATE_RESUME_LABEL = 'Chạy tiếp';
export const VOLUME_GATE_ACCEPT_LABEL = 'Chạy tiếp, để lỗi xem sau';
export const VOLUME_GATE_OPEN_SOURCE_LABEL = 'Mở Nguồn để sửa chương';
/** Thông báo khi «Chạy tiếp» mà BE trả `jobId null` + `waiting 'REVIEW_GATE'` (quyển chưa sạch giữ nguyên, không có gì để chạy lại). */
export const VOLUME_GATE_NOTHING_TO_RERUN = 'Chưa có gì để chạy lại: các quyển còn lỗi vẫn nguyên như lúc chấm. Sửa chương ở Nguồn rồi bấm “Chạy tiếp”, hoặc bấm “Chạy tiếp, để lỗi xem sau”.';

const MAX_VOLUME_LINES = 5;
const trim = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export interface VolumeGateWaitView {
  label: string;
  /** Câu chính: «Có N quyển chưa sạch sau khi máy chấm và quét lại». */
  sentence: string;
  /** Mỗi quyển một dòng (tối đa 5; thừa ⇒ «…và M quyển nữa»). */
  volumeLines: string[];
  /** Gợi ý làm gì tiếp. */
  hint: string;
  /** Số quyển còn lỗi thật (UNRESOLVED) và số quyển máy chưa chấm được (GATE_ERROR). */
  unresolvedCount: number;
  errorCount: number;
  confirm: { title: string; content: string; okText: string };
}

/** «chương 31–60» / «chương 7» / '' (không tra được). */
export const chapterRangeText = (r: [number, number] | null | undefined): string => {
  if (!r || !Number.isFinite(r[0]) || !Number.isFinite(r[1])) return '';
  return r[0] === r[1] ? `chương ${r[0]}` : `chương ${r[0]}–${r[1]}`;
};

/** Chữ đơn vị lỗi: `volume` ⇒ «cả quyển» · `chapter:12` ⇒ «chương 12» · lạ ⇒ chữ gốc đã rút gọn. */
export const unitText = (unit: string): string => {
  if (unit === 'volume') return 'cả quyển';
  const m = /^chapter:(\d+)$/.exec(unit);
  return m ? `chương ${m[1]}` : trim(String(unit ?? ''), 40);
};

const volumeLine = (it: VolumeGateViewItem): string => {
  const range = chapterRangeText(it.chapterRange);
  const head = `Quyển ${it.volumeIndex}${range ? ` (${range})` : ''}`;
  const first = it.issues[0];
  if (it.kind === 'GATE_ERROR') {
    // Chữ lỗi đã được BE làm sạch (không lộ lỗi thô); thiếu ⇒ câu chung.
    return `${head} — ${first ? trim(String(first.problem ?? ''), 120) : 'Máy chưa chấm được quyển này'} (sẽ chấm lại khi bấm “Chạy tiếp”)`;
  }
  if (!first) return `${head} — còn lỗi chưa gỡ`;
  const more = it.issues.length - 1;
  return `${head} — ${unitText(first.unit)}: ${trim(String(first.problem ?? ''), 140)}${more > 0 ? ` (+${more} lỗi nữa)` : ''}`;
};

/** Thẻ `WAIT_REVIEW_GATE` trước khi lưu khung xương. `vg` vắng (BE cũ / dữ liệu hỏng) ⇒ thẻ an toàn, không ném. */
export const volumeGateWaitView = (vg: VolumeGateView | null | undefined): VolumeGateWaitView => {
  const items = (vg?.items ?? []).filter((i) => i.kind !== 'RUNNING');
  const errorCount = items.filter((i) => i.kind === 'GATE_ERROR').length;
  const unresolvedCount = items.length - errorCount;
  const total = items.length;
  let sentence: string;
  if (total === 0) sentence = 'Cổng AI quét quyển chưa cho qua';
  else if (errorCount === 0) sentence = `Còn ${unresolvedCount} quyển chưa sạch sau khi máy chấm và quét lại`;
  else if (unresolvedCount === 0) sentence = `Máy chưa chấm được ${errorCount} quyển (lỗi lúc chấm)`;
  else sentence = `Còn ${unresolvedCount} quyển chưa sạch và ${errorCount} quyển máy chưa chấm được`;
  const shown = items.slice(0, MAX_VOLUME_LINES).map(volumeLine);
  const volumeLines = [...shown, ...(total > MAX_VOLUME_LINES ? [`…và ${total - MAX_VOLUME_LINES} quyển nữa`] : [])];
  const hint = errorCount > 0 && unresolvedCount === 0
    ? 'Bấm “Chạy tiếp” để máy chấm lại các quyển này.'
    : 'Sửa các chương nêu trên ở Nguồn rồi bấm “Chạy tiếp” (máy chỉ quét/chấm lại quyển đã đổi), hoặc bấm “Chạy tiếp, để lỗi xem sau”.';
  return {
    label: VOLUME_GATE_LABEL, sentence, volumeLines, hint, unresolvedCount, errorCount,
    confirm: {
      title: 'Chạy tiếp, để lỗi xem sau?',
      content: total > 0
        ? `Máy sẽ dựng khung xương ngay với ${total} quyển còn lỗi. Việc này được ghi lại (người bấm, lúc bấm); không gọi thêm AI cho cổng quét quyển.`
        : 'Máy sẽ dựng khung xương ngay mà không chờ cổng quét quyển. Việc này được ghi lại (người bấm, lúc bấm); không gọi thêm AI cho cổng.',
      okText: VOLUME_GATE_RESUME_LABEL,
    },
  };
};

/** Dòng chữ dưới dải (thẻ + mỗi quyển + gợi ý). */
export const volumeGateBarLines = (vg: VolumeGateView | null | undefined): string[] => {
  const v = volumeGateWaitView(vg);
  return [v.sentence, ...v.volumeLines, v.hint];
};

export interface ChapterGateNote { code: 'VOLUME_GATE'; label: string; lines: string[] }

/**
 * Lỗi cổng ① gắn vào CHƯƠNG (`unit 'chapter:N'`) ⇒ nhãn ở cột «Tình trạng» của bảng chương (cùng chỗ với lỗi dò chương, không thêm khối UI mới).
 * Trả `Map<orderIndex, ChapterGateNote>`. Quyển `GATE_ERROR`/`RUNNING` KHÔNG gắn (lỗi hạ tầng không phải lỗi của chương).
 */
export const chapterGateNotes = (vg: VolumeGateView | null | undefined): Map<number, ChapterGateNote> => {
  const out = new Map<number, ChapterGateNote>();
  for (const it of vg?.items ?? []) {
    if (it.kind !== 'UNRESOLVED') continue;
    for (const iss of it.issues) {
      const m = /^chapter:(\d+)$/.exec(iss.unit ?? '');
      if (!m) continue;
      const n = Number(m[1]);
      const line = `Quyển ${it.volumeIndex}: ${trim(String(iss.problem ?? ''), 200)}`;
      const cur = out.get(n);
      if (cur) cur.lines.push(line);
      else out.set(n, { code: 'VOLUME_GATE', label: 'lỗi quét quyển', lines: [line] });
    }
  }
  return out;
};
